import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useColors } from '../../theme/ColorModeContext';

interface SeasonCardProps {
  season: number;
  solved: number;
  total: number;
}

const SeasonCard = ({ season, solved, total }: SeasonCardProps) => {
  const colors = useColors();
  const pct = total > 0 ? Math.round((solved / total) * 100) : 0;
  const completed = pct === 100;

  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '12px 14px',
      borderRadius: '8px',
      border: `1px solid ${completed ? colors.accent.purple : colors.border.subtle}`,
      backgroundColor: completed
        ? 'rgba(124, 77, 255, 0.08)'
        : 'rgba(255, 255, 255, 0.02)',
      boxShadow: completed
        ? `0 0 16px rgba(124, 77, 255, 0.2)`
        : 'none',
      transition: 'all 0.3s ease',
    }}>
      {/* Icon */}
      <Box sx={{
        width: 36,
        height: 36,
        borderRadius: '8px',
        backgroundColor: 'rgba(124, 77, 255, 0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <ShieldIcon sx={{ color: colors.accent.purple, fontSize: 20 }} />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
          <Typography sx={{
            fontSize: '13px',
            fontWeight: 600,
            fontFamily: '"Orbitron", sans-serif',
            color: colors.text.primary,
          }}>
            Season {season}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {completed && (
              <CheckCircleIcon sx={{ color: colors.accent.purple, fontSize: 14 }} />
            )}
            <Typography sx={{ fontSize: '12px', color: colors.text.secondary }}>
              {solved}<Typography component="span" sx={{ color: colors.text.muted, fontSize: '12px' }}>/{total}</Typography>
            </Typography>
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(124, 77, 255, 0.12)',
            '& .MuiLinearProgress-bar': {
              borderRadius: 2,
              background: `linear-gradient(90deg, ${colors.accent.purple} 0%, ${colors.accent.cyan} 100%)`,
              boxShadow: completed ? `0 0 8px rgba(124, 77, 255, 0.5)` : 'none',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default SeasonCard;
